import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LogOut, LayoutGrid, Building2, Users2, UserCheck, Mail, Wallet, MessageSquare, BookOpen } from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "../../firebaseConfig";

const navItems = [
  { to: "/admin-dashboard", label: "Overview", icon: LayoutGrid },
  { to: "/admin/companies", label: "Companies", icon: Building2 },
  { to: "/admin/companies/payments", label: "Bulk Payments", icon: Building2 },
  { to: "/admin/farmers", label: "Farmers", icon: UserCheck },
  { to: "/admin/customers", label: "Customers", icon: Users2 },
  { to: "/admin/company-messages", label: "Company Messages", icon: MessageSquare },
  { to: "/admin/b2c-transactions", label: "B2C Payments", icon: Wallet },
  { to: "/admin/accounting", label: "Accounting", icon: BookOpen },
  { to: "/admin/messages", label: "Landing Messages", icon: Mail },
];

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const updateViewport = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 bg-white shadow-md border-r border-slate-100 overflow-hidden transition-[transform,width] duration-200 ${
          sidebarOpen ? "w-64 translate-x-0" : "w-0 -translate-x-full lg:w-0 lg:-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Neuracraft</p>
            <p className="text-lg font-semibold text-slate-800">Admin</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-500 hover:text-slate-800"
            aria-label="Close sidebar"
          >
            ✕
          </button>
        </div>
        <nav className="p-4 space-y-2">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
              onClick={() => isMobile && setSidebarOpen(false)}
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 pb-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-2 rounded-lg text-sm font-semibold hover:bg-slate-800 transition"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-0">
        <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-100 px-4 lg:px-8 py-4 flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Control Center</p>
            <h1 className="text-xl font-semibold text-slate-800">Admin Dashboard</h1>
          </div>
          <button
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg shadow-sm hover:bg-slate-50"
            onClick={() => setSidebarOpen((prev) => !prev)}
          >
            {sidebarOpen ? "Hide sidebar" : "Show sidebar"}
          </button>
        </header>

        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
